import type { ViewKind } from './nav';

/**
 * الجولات التعريفية: لكل شاشة خطوات قصيرة بلغة صاحب شركة لا محاسب. البيع لشركات كثيرة
 * يعنى أن الشرح يجب أن يكون داخل البرنامج نفسه، لا مكالمة مع البائع لكل عميل جديد.
 *
 * `target` محدد CSS (أو قائمة، يُؤخذ أول عنصر ظاهر — للموبايل حيث القائمة درج مخفى)،
 * و`text` يقصر المطابقة على عنصر يحتوى هذا النص (عنوان لوحة مثلاً). خطوة بلا هدف ظاهر
 * تُعرض فى منتصف الشاشة، فلا تنكسر الجولة لو لم تُحمَّل البيانات بعد أو اختلفت الصلاحيات.
 */
export interface TourStep {
  target?: string | readonly string[];
  text?: string;
  /** تُحذف الخطوة إن لم يظهر عنصرها عند بدء الجولة. */
  optional?: boolean;
  title: string;
  body: string;
}

/** معرّف الجولة: نوع الشاشة، أو تبويب داخل الإعدادات. */
export type TourId = ViewKind | 'settings:einvoice' | 'settings:members' | 'settings:password';

const NAV = ['aside .nav-sections', '.nav-toggle'];
const HEADER = '.page-header';
const ACTIONS = '.form-actions';

/** جولة الترحيب: تسبق أول جولة شاشة مرة واحدة، وتعرّف بالهيكل العام. */
export const WELCOME: readonly TourStep[] = [
  {
    title: 'أهلاً بك فى حسابات نواة',
    body: 'جولة قصيرة تعرّفك بالبرنامج. لا تحتاج أن تكون محاسبًا: كل شاشة تشرح نفسها، وتحت كل حقل سطر رمادى يقول لك ماذا تكتب.',
  },
  {
    target: NAV,
    title: 'القائمة',
    body: 'كل أقسام البرنامج هنا مرتبة حسب شغلك اليومى: المبيعات، المشتريات، المخزون، الخزينة، ثم المحاسبة والتقارير.',
  },
  {
    target: '.tour-help',
    title: 'زر المساعدة',
    body: 'فى أى شاشة اضغط هنا لتعيد جولتها، أو لتبدأ الجولة الكاملة من جديد.',
  },
  {
    target: '.topbar .who .icon-button',
    title: 'الوضع الفاتح والداكن',
    body: 'غيّر شكل الشاشة حسب راحة عينك. «تلقائى» يتبع إعداد جهازك.',
  },
];

export const TOURS: Record<TourId, readonly TourStep[]> = {
  dashboard: [
    {
      target: '.dash-hero',
      title: 'الرئيسية',
      body: 'أول ما تراه كل يوم: ملخص سريع لحال شركتك، بلا حاجة لفتح أى تقرير.',
    },
    {
      target: '.dash-controls',
      optional: true,
      title: 'اختر الفترة',
      body: 'هذا الشهر، الربع، منذ بداية السنة، أو فترة تختارها — كل الأرقام تحتها تتغير حسب اختيارك.',
    },
    {
      target: '.dash-kpis',
      optional: true,
      title: 'المؤشرات',
      body: 'المبيعات والمصروفات والربح والنقدية. السهم بجانب الرقم يقارنه بالفترة السابقة.',
    },
    {
      target: '.dash-todos',
      optional: true,
      title: 'مهام اليوم',
      body: 'ما يحتاج تصرفك الآن: فواتير متأخرة، سداد مستحق، فترة تنتظر الإقفال.',
    },
    {
      target: '.dash-insights-panel',
      optional: true,
      title: 'تنبيهات ذكية',
      body: 'البرنامج يراقب أرقامك وينبّهك لما يستحق الانتباه. اضغط أى تنبيه لتفتح تفاصيله.',
    },
    {
      target: '.dash-empty',
      optional: true,
      title: 'شركة جديدة؟',
      body: 'ابدأ من «إعداد الشركة»: البيانات، الهوية، والأرصدة التى كانت عندك قبل البرنامج.',
    },
  ],
  customers: [
    { target: HEADER, title: 'العملاء', body: 'كل من تبيع له: بياناته، وكم عليه لك، وكشف حسابه.' },
    {
      target: '.panel',
      text: 'عميل جديد',
      title: 'أضف عميلاً',
      body: 'الاسم يكفى للبداية. الرقم الضريبى ضرورى فقط لو ستُصدر له فاتورة إلكترونية كشركة.',
    },
    {
      target: '.panel',
      text: 'العملاء',
      title: 'قائمة العملاء',
      body: 'الرصيد = ما عليه لك الآن. اضغط على أى عميل لترى كشف حسابه: كل فاتورة وكل دفعة.',
    },
  ],
  invoices: [
    {
      target: HEADER,
      title: 'فواتير المبيعات',
      body: 'هنا تبيع: تختار العميل والأصناف، والبرنامج يحسب الإجمالى ويسجّل كل شىء فى الحسابات وحده.',
    },
    {
      target: 'form',
      title: 'فاتورة جديدة',
      body: 'اختر العميل، ثم أضف سطرًا لكل صنف أو خدمة بالكمية والسعر.',
    },
    {
      target: ACTIONS,
      title: 'إصدار الفاتورة',
      body: 'بعد الإصدار تأخذ رقمًا نهائيًا ولا تُعدَّل. لو أخطأت: «إشعار دائن» يلغيها أو يخفّضها.',
    },
    {
      target: '.panel',
      text: 'الفواتير',
      title: 'الفواتير السابقة',
      body: 'كل فاتورة بالمتبقى منها. من هنا تطبعها، أو تعمل عليها مرتجعًا أو خصمًا.',
    },
  ],
  receipts: [
    {
      target: HEADER,
      title: 'التحصيل',
      body: 'سجّل كل مبلغ استلمته من عميل — نقدًا أو تحويلاً أو شيكًا.',
    },
    {
      target: '.panel',
      text: 'تحصيل من عميل',
      title: 'تحصيل جديد',
      body: 'اختر العميل والمبلغ وأين دخلت الفلوس (الخزينة أو البنك).',
    },
    {
      target: '.panel',
      text: 'الفواتير المفتوحة',
      title: 'وزّع المبلغ على الفواتير',
      body: 'حدد أى فاتورة يسددها هذا المبلغ. لو دفع العميل مقدمًا، اتركه وسوّه على فاتورته لاحقًا.',
    },
    {
      target: '.panel',
      text: 'الإيصالات',
      title: 'الإيصالات',
      body: 'كل تحصيل سجلته. «تسوية على فاتورة» تربط مقدمًا قديمًا بفاتورة جديدة.',
    },
  ],
  suppliers: [
    {
      target: HEADER,
      title: 'الموردون',
      body: 'كل من تشترى منه: بياناته، وكم له عندك، وكشف حسابه.',
    },
    {
      target: '.panel',
      text: 'مورد جديد',
      title: 'أضف موردًا',
      body: 'الاسم ومدة السداد كافيان للبداية.',
    },
    {
      target: '.panel',
      text: 'الموردون',
      title: 'قائمة الموردين',
      body: 'الرصيد = ما عليك له الآن. اضغط على أى مورد لترى كشف حسابه.',
    },
  ],
  bills: [
    {
      target: HEADER,
      title: 'فواتير الموردين',
      body: 'سجّل هنا كل فاتورة شراء تصلك: بضاعة أو خدمة أو مصروف.',
    },
    {
      target: 'form',
      title: 'فاتورة مورد',
      body: 'انقل البيانات من الفاتورة الورقية كما هى: المورد، رقم فاتورته، التاريخ، والسطور.',
    },
    {
      target: ACTIONS,
      title: 'تسجيل الفاتورة',
      body: 'بعد التسجيل تُضاف لما عليك للمورد، والأصناف المخزنية تدخل المخزون تلقائيًا.',
    },
  ],
  payments: [
    { target: HEADER, title: 'السداد للموردين', body: 'سجّل كل مبلغ دفعته لمورد.' },
    {
      target: '.panel',
      text: 'سداد لمورد',
      title: 'سداد جديد',
      body: 'اختر المورد والمبلغ ومن أين دفعت (الخزينة أو البنك).',
    },
    {
      target: '.panel',
      text: 'الفواتير المفتوحة',
      title: 'على أى فاتورة؟',
      body: 'حدد الفواتير التى يسددها هذا المبلغ، فيعرف البرنامج ما بقى عليك بالضبط.',
    },
  ],
  catalog: [
    {
      target: HEADER,
      title: 'الأصناف والخدمات',
      body: 'كل ما تبيعه أو تشتريه. عرّفه مرة واحدة، ثم اختره فى الفواتير بضغطة.',
    },
    {
      target: 'form',
      title: 'صنف جديد',
      body: '«مخزنى» للبضاعة التى تُعدّ على الرف، و«خدمة» لما لا كمية له. الحسابات اتركها على «الافتراضى».',
    },
    {
      target: '.panel',
      text: 'الأصناف والخدمات',
      title: 'القائمة',
      body: 'اضغط أى صنف لتعدّله. الصنف القديم لا يُحذف — أوقفه فيختفى من الفواتير الجديدة.',
    },
  ],
  inventory: [
    {
      target: HEADER,
      title: 'المخزون',
      body: 'كم عندك من كل صنف، وبكم تكلفته، وكل حركة دخول وخروج.',
    },
    {
      target: '.tabs',
      title: 'الأقسام',
      body: 'الرصيد والتقييم، كارت الصنف، الجرد، والرصيد الافتتاحى — كل واحد فى تبويب.',
    },
    {
      title: 'الجرد',
      body: 'مرة كل فترة عدّ البضاعة فعليًا، واكتب العدد فى تبويب الجرد. البرنامج يصحح الكميات ويسجّل الفرق.',
    },
  ],
  treasury: [
    {
      target: HEADER,
      title: 'الخزينة',
      body: 'تحويل الفلوس بين خزائنك وبنوكك: إيداع نقدية فى البنك، أو سحب منه للخزينة.',
    },
    {
      target: 'form',
      title: 'تحويل جديد',
      body: 'من أين خرجت الفلوس، وإلى أين دخلت، وكم. إجمالى فلوسك لا يتغير — فقط مكانها.',
    },
  ],
  expenses: [
    {
      target: HEADER,
      title: 'المصروفات النقدية',
      body: 'مصروفات تدفعها مباشرة بلا فاتورة مورد: كهرباء، إيجار، بوفيه، مواصلات.',
    },
    {
      target: 'form',
      title: 'مصروف جديد',
      body: 'اختر من أين دفعت، ثم سطرًا لكل بند بمبلغه. يظهر فى مصروفات الشهر وتقرير الربح.',
    },
  ],
  treasuryAccounts: [
    {
      target: HEADER,
      title: 'الخزائن والبنوك',
      body: 'عرّف هنا كل مكان تحفظ فيه فلوسك: خزينة المحل، كل حساب بنكى، فودافون كاش.',
    },
    {
      target: '.panel',
      text: 'إضافة',
      title: 'أضف خزينة أو بنكًا',
      body: 'اسم واضح يكفى (مثل «بنك مصر — جارى»). بعدها يظهر فى كل شاشات القبض والصرف.',
    },
  ],
  reconciliation: [
    {
      target: HEADER,
      title: 'المطابقة البنكية',
      body: 'تتأكد أن رصيد البنك فى البرنامج يساوى الرصيد فى كشف البنك الحقيقى.',
    },
    {
      target: 'form',
      title: 'كيف تطابق',
      body: 'اختر البنك، واكتب تاريخ ورصيد آخر كشف وصلك، ثم علّم كل حركة ظهرت فى الكشف. الفرق يجب أن يصبح صفرًا.',
    },
  ],
  journal: [
    {
      target: HEADER,
      title: 'قيد يومية',
      body: 'للحالات الخاصة فقط. الفواتير والتحصيل والسداد تسجّل قيودها وحدها — لا تحتاج هذه الشاشة يوميًا.',
    },
    {
      target: 'form',
      title: 'المدين والدائن',
      body: 'كل قيد له طرفان متساويان: من أين جاء المبلغ وإلى أين ذهب. لو لم تكن متأكدًا، اسأل محاسبك قبل الترحيل.',
    },
    {
      target: ACTIONS,
      title: 'الترحيل',
      body: 'الزر لا يعمل إلا إذا تساوى الطرفان. بعد الترحيل لا يُعدَّل القيد — يُصحَّح بقيد عكسى.',
    },
  ],
  ledger: [
    {
      target: HEADER,
      title: 'كشف حساب',
      body: 'اختر أى حساب من دليل الحسابات لترى كل حركة عليه ورصيده بعد كل حركة.',
    },
    {
      title: 'دليل الحسابات',
      body: 'هو قائمة «الأدراج» التى تُصنَّف فيها كل الفلوس: النقدية، العملاء، المبيعات، المصروفات… البرنامج يختار الدرج الصحيح تلقائيًا فى كل فاتورة.',
    },
  ],
  periods: [
    {
      target: HEADER,
      title: 'الفترات المالية',
      body: 'السنة مقسمة لفترات (شهور عادةً). بعد مراجعة أرقام الفترة، أقفلها فلا يتغير فيها شىء بالخطأ.',
    },
    {
      target: 'table',
      title: 'الإقفال',
      body: 'زر «إقفال…» يعرض قائمة فحص أولاً: ما الناقص قبل الإقفال. إعادة الفتح ممكنة للمالك بسبب مكتوب.',
    },
    {
      title: 'نهاية السنة',
      body: 'فى آخر فترة يظهر «قيد إقفال السنة»: ينقل ربح السنة أو خسارتها، ويجهّز السنة الجديدة. راجعه مع محاسبك.',
    },
  ],
  reports: [
    {
      target: HEADER,
      title: 'القوائم المالية',
      body: 'التقارير الأساسية التى يطلبها البنك ومصلحة الضرائب والمحاسب.',
    },
    {
      target: '.tabs',
      title: 'اختر التقرير',
      body: '«قائمة الدخل» = هل كسبت أم خسرت. «الميزانية» = ما تملكه وما عليك. «الأعمار» = من تأخر فى الدفع.',
    },
    {
      title: 'الطباعة والتصدير',
      body: 'كل تقرير يُطبع أو يُنزَّل Excel لترسله لمحاسبك.',
    },
  ],
  reportsCenter: [
    {
      target: HEADER,
      title: 'مركز التقارير',
      body: 'كل التقارير التفصيلية فى مكان واحد: المبيعات، المشتريات، المخزون، الأرصدة، والسجلات.',
    },
    {
      title: 'الفلاتر',
      body: 'اختر تقريرًا، ثم حدد الفترة أو العميل أو الصنف. الأرقام تتحدث فورًا ويمكن تصديرها.',
    },
  ],
  setup: [
    {
      target: HEADER,
      title: 'إعداد الشركة',
      body: 'خطوات بالترتيب لتجهيز شركتك. يمكنك التوقف والرجوع فى أى وقت — ما حفظته يبقى.',
    },
    {
      target: '.wizard-steps, .wizard',
      title: 'الخطوات',
      body: 'البيانات القانونية، ثم الهوية (اللوجو والألوان)، ثم السنة المالية، ثم الأرصدة الافتتاحية، ثم المستخدمين.',
    },
  ],
  migration: [
    {
      target: HEADER,
      title: 'ترحيل البيانات',
      body: 'لو كنت تعمل على Excel أو برنامج آخر، انقل بياناتك من هنا بدل إدخالها يدويًا.',
    },
    {
      title: 'كيف يعمل',
      body: 'نزّل قالب Excel، املأه أو ارفع ملفك كما هو، اربط الأعمدة، راجع الأرقام، ثم اعتمد. لا يدخل شىء الحسابات قبل اعتمادك.',
    },
    {
      target: '.panel',
      text: 'دفعات الترحيل',
      title: 'الدفعات',
      body: 'كل رفعة ملفات «دفعة» لها تقرير. لو اكتشفت خطأ بعد الاعتماد، «عكس الدفعة» يلغيها كاملة.',
    },
  ],
  settings: [
    {
      target: '.tabs',
      title: 'الإعدادات',
      body: 'بيانات الشركة، الفاتورة الإلكترونية، المستخدمون، وكلمة السر — كل واحد فى تبويب.',
    },
    {
      target: 'form',
      title: 'بيانات الشركة والخيارات',
      body: 'الاسم والرقم الضريبى يظهران على فواتيرك. باقى الخيارات مضبوطة على الأنسب لمعظم الشركات.',
    },
  ],
  'settings:einvoice': [
    {
      target: '.tabs',
      title: 'الفاتورة الإلكترونية',
      body: 'اربط البرنامج بمنظومة الضرائب المصرية لتُرسل فواتيرك إلكترونيًا.',
    },
    {
      title: 'خطوات الربط',
      body: 'سجّل البرنامج فى بوابة الضرائب، انسخ المفاتيح هنا، جرّب على بيئة الاختبار، ثم فعّل الإنتاج. اطلب مساعدة محاسبك فى أول مرة.',
    },
  ],
  'settings:members': [
    {
      target: '.panel',
      text: 'المستخدمون',
      title: 'المستخدمون',
      body: 'كل من يعمل على البرنامج معك. لكل واحد حسابه الخاص — لا تشارك كلمة سرك.',
    },
    {
      target: '.panel',
      text: 'إضافة مستخدم',
      title: 'أضف عضوًا',
      body: 'اختر له دورًا: محاسب، مُدخل بيانات، أو مشاهد فقط. الدور يحدد ما يراه وما يفعله.',
    },
    {
      target: '.panel',
      text: 'الأدوار',
      title: 'أدوار مخصصة',
      body: 'لو الأدوار الجاهزة لا تناسب، اصنع دورًا وعلّم صلاحياته بنفسك.',
    },
  ],
  'settings:password': [
    {
      target: 'form',
      title: 'كلمة السر',
      body: 'غيّرها لو شككت أن أحدًا عرفها. بعد التغيير تخرج كل الأجهزة الأخرى تلقائيًا.',
    },
  ],
  platform: [
    {
      target: HEADER,
      title: 'لوحة المنصة',
      body: 'لك أنت كبائع: كل الشركات المشتركة، باقاتها، وحالتها.',
    },
    {
      target: '.panel',
      text: 'إضافة شركة',
      title: 'شركة جديدة',
      body: 'أنشئ الشركة ومالكها الأول بكلمة سر أولى تسلّمها له. عند أول دخول يفعّل التحقق بخطوتين.',
    },
  ],
};

const STORAGE_KEY = 'hesabat.tours.v1';

/** الجولات التى رآها هذا المستخدم على هذا الجهاز — راحة لكل مشاهد، لا حالة مشتركة. */
export function readSeen(storage: Pick<Storage, 'getItem'> | null): Set<string> {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function markSeen(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  ids: string[],
): void {
  try {
    const seen = readSeen(storage);
    ids.forEach((id) => seen.add(id));
    storage?.setItem(STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // تخزين محجوب (تصفح خاص): تعود الجولة فى الزيارة التالية، ولا شىء ينكسر
  }
}

export function resetSeen(storage: Pick<Storage, 'removeItem'> | null): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // لا شىء
  }
}

/** خطوات التشغيل التلقائى: الترحيب مرة واحدة فى أول جولة، ثم جولة الشاشة مرة لكل شاشة. */
export function autoTour(
  id: TourId,
  seen: Set<string>,
): { steps: TourStep[]; ids: string[] } | null {
  if (seen.has(id)) return null;
  const screen = TOURS[id];
  if (seen.has('welcome')) return { steps: [...screen], ids: [id] };
  return { steps: [...WELCOME, ...screen], ids: ['welcome', id] };
}

export function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
